import { Checkbox, Layout, ListItemCustom, ProgressIndicator, Text, TextLink } from '@oneaudi/unified-web-components';
import { useI18n } from '~/app/i18n/i18n';
import { VATConfig } from '~/app/market-config';
import placeholder from '~/assets/icons/placeholder.svg';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { ItemListStyle } from '~/components/item-list/styles/item-list-style';
import { Prices } from '~/components/prices';

import { AccessoriesItem } from '~/graphql/mapper/map-accessories';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { isDisclaimerAvailable } from '~/utils/date';

export type CardItemInfo = AccessoriesItem & {
	disabled: boolean;
	selected: boolean;
	loading: boolean;
	standard: boolean;
	hasDetails: boolean;
};

interface ItemCardProps {
	item: CardItemInfo;
	feature: VATConfig;
	standardPriceText: string;
	onChange: (status: boolean, oldStatus: boolean) => void;
	moreInformation: {
		title: string;
		onMoreInformation: (event: React.MouseEvent) => void;
	};
}

export const ItemList = ({ item, onChange, moreInformation, standardPriceText, feature }: ItemCardProps) => {
	const { i18n } = useI18n();

	return (
		<ListItemCustom>
			<ItemListStyle data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER} hasImage={!!item.imageUrl}>
				<Layout direction="column" justify="space-between" rowGap="15px" className="details-wrapper">
					<Layout rowGap="15px" direction="column" className="checkbox-container">
						<Checkbox
							label={item.name}
							name={item.name}
							id={`equipment-checkbox-${item.pr7}`}
							disabled={item.disabled}
							value={item.pr7}
							checked={item.selected}
							onChange={() => onChange(!item.selected, item.selected)}
							data-testid={DATA_TEST_ID.SELECTION_CARD_CHECKBOX}
						/>
						{item.totalPrice &&
							(item.standard ? (
								<>
									{/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
									<div tabIndex={0}>
										<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
											{standardPriceText}
										</Text>
									</div>
								</>
							) : (
								<Prices totalPrice={item.totalPrice} pr7={item.pr7} vatConfig={feature} />
							))}
					</Layout>
					<div>
						{item.texts?.disclaimerText &&
							isDisclaimerAvailable(item.texts.disclaimerStartDate, item.texts.disclaimerEndDate) && (
								<div className="disclaimer-wrapper">
									<Disclaimer
										type="item"
										headline={item.texts.disclaimerHeadline || ''}
										content={item.texts.disclaimerText}
									/>
								</div>
							)}
						{item.hasDetails && (
							<TextLink
								aria-label={`${moreInformation.title} ${i18n('ngc.a11y.about')} ${item.name}`}
								className="more-info"
								onClick={moreInformation.onMoreInformation}
								size="sm"
								variant="standalone">
								{moreInformation.title}
							</TextLink>
						)}
					</div>
				</Layout>
				<div className="image-container">
					{item.imageUrl && (
						<img
							loading="lazy"
							src={item.imageUrl || placeholder}
							alt={item.imageUrl ? item.name : i18n('ngc.a11y.audiLogo')}
						/>
					)}
				</div>

				{item.loading && (
					<div className="loading-skeleton">
						<ProgressIndicator size={'lg'} variant={'spinner'} type={'infinite'} />
					</div>
				)}
			</ItemListStyle>
		</ListItemCustom>
	);
};
